import { AddressField, Message } from '@flos/react-ui';
import type { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useState } from 'react';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { type CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useHippoText } from '../../../../hooks/useHippoText';
import { getAccountHolderDataSelector } from '../../../../recoil/selectors/getAccountHolderDataSelector';
import { getDawaIdFromkvhxUrl } from '../../../../utils/addressUtil';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { track } from '../../../../utils/trackingUtil';
import CallMeModal from '../../../../shared_components/CallMeModal/CallMeModal';
import { prhBBRAtom } from '../../../../recoil/atoms/prhBBRAtom';
import { refreshBbrSelectorAtom } from '../../../../recoil/atoms/refreshBbrSelectorAtom';
import { COVERABLE_CODE } from '../../../../constants/coverable';
import HelpLink from '../../../../shared_components/HelpLink/HelpLink';

const HouseAddressStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const getHippoText = useHippoText();
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const resetBBR = useResetRecoilState(prhBBRAtom);
    const refreshBbrData = useSetRecoilState(refreshBbrSelectorAtom);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [isLoading, setIsLoading] = useState(false);
    const houseCoverables =
        currentProduct.flavourCode === COVERABLE_CODE.prhHouse
            ? coverables.houses ?? []
            : coverables.holidayHomes ?? [];
    const coverableIndex = houseCoverables?.findIndex(
        (coverable) => coverable.fixedId === currentProduct.fixedId
    );
    const currentHouseCoverable = houseCoverables[coverableIndex];
    const accountData = useRecoilValue(getAccountHolderDataSelector);
    const address = currentHouseCoverable.address ?? accountData.address;
    const [isValid, setIsValid] = useState<boolean>(!!address);
    const [showCallMeModal, setShowCallMeModal] = useState<boolean>(false);

    const updateAddress = (fieldEventDataProps: FieldEventDataProps): void => {
        if (!houseCoverables || fieldEventDataProps.kvhx === undefined) {
            return;
        }
        refreshBbrData({});
        resetBBR();
        const { address, kvhxUrl, kvhx } = fieldEventDataProps;
        const dawaId = getDawaIdFromkvhxUrl(kvhxUrl);
        updateCoverableInformation({
            ...currentHouseCoverable,
            dawaId,
            kvhxId: kvhx,
            address
        });
    };

    const onNext = (): void => {
        setIsLoading(true);
        const houseType =
            currentProduct.flavourCode === COVERABLE_CODE.prhHouse ? 'house' : 'holidayHome';

        const onsaPrhCoverables = [
            ...(coverables.houses ?? []),
            ...(coverables.holidayHomes ?? [])
        ];
        const filledAgeOfOldest =
            onsaPrhCoverables.find(
                (coverable) =>
                    (coverable.yearlyPrice || coverable.monthlyPrice) &&
                    coverable.ageOfOldestInHousehold
            )?.ageOfOldestInHousehold ?? null;

        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [houseType]: {
                publicId: currentHouseCoverable.publicId,
                fixedId: currentHouseCoverable.fixedId,
                kvhxId: currentHouseCoverable.kvhxId,
                dawaId: currentHouseCoverable.dawaId ?? accountData.dawaId,
                name: currentHouseCoverable.address ?? accountData.address,
                ageOfOldestInHousehold: filledAgeOfOldest
            },
            productToUpdate: currentProduct.flavourName
        };

        updateCoverableInformation({
            ...currentHouseCoverable,
            name: currentHouseCoverable.address ?? accountData.address,
            ageOfOldestInHousehold:
                filledAgeOfOldest ?? currentHouseCoverable.ageOfOldestInHousehold
        });

        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${houseType}CoverableInformationHouseAddress`, [
                {
                    dawaId: currentHouseCoverable.dawaId ?? accountData.dawaId
                },
                {
                    address: currentHouseCoverable.address ?? accountData.address
                }
            ]);
            goNext();
            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate onNext={onNext} disableNext={isLoading || !isValid} hidePrevious>
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-ADDRESS-HEADER-PRH')}
            >
                <AddressField
                    fieldEvent={(data) => {
                        updateAddress(data);
                        if (data.status) setIsValid(data.status === 'valid');
                    }}
                    defaultValue={address}
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-ADDRESS-INPUTFIELD')}
                />
                <Message
                    className="coverage-warning paragraph--small u-spacing-stack-s"
                    title={
                        currentProduct.flavourCode === COVERABLE_CODE.prhHouse
                            ? getHippoText(
                                  'DSS-COVERABLE-INFORMATION-STEP-ADDRESS-INFORMATION-HOUSE'
                              )
                            : getHippoText(
                                  'DSS-COVERABLE-INFORMATION-STEP-ADDRESS-INFORMATION-HOLIDAY-HOME'
                              )
                    }
                />
                <div className="u-display-flex u-flex-justify-end">
                    <HelpLink
                        onClick={() => {
                            setShowCallMeModal(true);
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-INFORMATION-STEP-ADDRESS-CANNOT-FIND-ADDRESS')}
                    </HelpLink>
                </div>
            </CoverableDetailsTemplate>
            <CallMeModal
                activityCode="dss_call_request"
                showModal={showCallMeModal}
                closeModal={() => {
                    setShowCallMeModal(false);
                }}
                title={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-HEADER')}
                description={
                    getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-SUBHEADER') +
                    ' ' +
                    getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-BOOK-CALL-SUBHEADER')
                }
            />
        </WizardPagetemplate>
    );
};

export default HouseAddressStep;
